<script setup>
import { computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  column: { type: Object, required: true },
  conversations: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  inboxes: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'drop-card',
  'remove',
  'rename',
  'add-conversation',
  'reorder-column',
  'open-card',
]);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { getPlainText } = useMessageFormatter();

const isDragOver = ref(false);
const isColumnDragOver = ref(false);
const isEditing = ref(false);
const editValue = ref('');
const titleInputRef = ref(null);
const confirmingRemove = ref(false);

const count = computed(() => props.conversations.length);

const startEdit = async () => {
  isEditing.value = true;
  editValue.value = props.column.title;
  await nextTick();
  titleInputRef.value?.focus();
  titleInputRef.value?.select();
};

const commitEdit = () => {
  const v = editValue.value.trim();
  if (v && v !== props.column.title) {
    emit('rename', { id: props.column.id, title: v });
  }
  isEditing.value = false;
};

const cancelEdit = () => {
  isEditing.value = false;
};

const confirmRemove = () => {
  if (confirmingRemove.value) {
    emit('remove', props.column.id);
    confirmingRemove.value = false;
  } else {
    confirmingRemove.value = true;
    setTimeout(() => {
      confirmingRemove.value = false;
    }, 3000);
  }
};

const getConversationPreview = conversation => {
  const { lastNonActivityMessage = {}, customAttributes = {} } = conversation;
  const subject = customAttributes?.email?.subject;
  return getPlainText(
    subject || lastNonActivityMessage?.content || t('CHAT_LIST.NO_CONTENT')
  );
};

const getContactName = conversation => conversation?.meta?.sender?.name || '';
const getContactThumbnail = conversation =>
  conversation?.meta?.sender?.thumbnail || '';
const getContactStatus = conversation =>
  conversation?.meta?.sender?.availabilityStatus;

const getAssignee = conversation => {
  const { meta: { assignee: agent = {} } = {} } = conversation || {};
  return {
    name: agent?.name ?? agent?.availableName,
    thumbnail: agent?.thumbnail,
    status: agent?.availabilityStatus,
  };
};

const getInboxIcon = conversation => {
  const inbox = props.inboxes[conversation.inbox_id];
  if (!inbox) return 'i-lucide-inbox';
  return getInboxIconByType(inbox.channelType, inbox.medium);
};

const getInboxName = conversation =>
  props.inboxes[conversation.inbox_id]?.name || '';

const getLastActivity = conversation => {
  const ts = conversation?.timestamp;
  return ts ? shortTimestamp(dynamicTime(ts)) : '';
};

const PRIORITY_META = {
  urgent: { color: 'bg-red-500', label: 'URGENT', icon: 'i-lucide-alert-triangle' },
  high: { color: 'bg-orange-500', label: 'HIGH', icon: 'i-lucide-arrow-up' },
  medium: { color: 'bg-yellow-500', label: 'MEDIUM', icon: 'i-lucide-equal' },
  low: { color: 'bg-blue-500', label: 'LOW', icon: 'i-lucide-arrow-down' },
};

const getPriorityMeta = conversation => PRIORITY_META[conversation?.priority];

const buildTooltip = conversation => {
  const lines = [
    `#${conversation.display_id} · ${getContactName(conversation)}`,
    `${t('KANBAN.ASSIGNED_TO')}: ${getAssignee(conversation).name || '—'}`,
    `${getInboxName(conversation)}`,
    getConversationPreview(conversation).slice(0, 120),
  ];
  return lines.filter(Boolean).join('\n');
};

const openConversation = (e, conversation) => {
  const path = frontendURL(
    conversationUrl({ accountId: route.params.accountId, id: conversation.id })
  );
  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};

// Drag & drop — cards
const onCardDragStart = (e, conversation) => {
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/kanban-card', String(conversation.id));
  e.dataTransfer.setData('text/kanban-from', props.column.id);
};

const onCardDragOver = e => {
  if (e.dataTransfer.types.includes('text/kanban-card')) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    isDragOver.value = true;
  }
};

const onCardDragLeave = e => {
  if (!e.currentTarget.contains(e.relatedTarget)) {
    isDragOver.value = false;
  }
};

const onCardDrop = e => {
  if (!e.dataTransfer.types.includes('text/kanban-card')) return;
  e.preventDefault();
  isDragOver.value = false;
  const conversationId = Number(e.dataTransfer.getData('text/kanban-card'));
  const fromColumnId = e.dataTransfer.getData('text/kanban-from');
  if (conversationId && fromColumnId !== props.column.id) {
    emit('drop-card', { conversationId, toColumnId: props.column.id });
  }
};

// Drag & drop — columns
const onColumnDragStart = e => {
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/kanban-column', props.column.id);
};

const onColumnDragOver = e => {
  if (e.dataTransfer.types.includes('text/kanban-column')) {
    e.preventDefault();
    isColumnDragOver.value = true;
  }
};

const onColumnDragLeave = () => {
  isColumnDragOver.value = false;
};

const onColumnDrop = e => {
  if (!e.dataTransfer.types.includes('text/kanban-column')) return;
  e.preventDefault();
  isColumnDragOver.value = false;
  const fromId = e.dataTransfer.getData('text/kanban-column');
  if (fromId && fromId !== props.column.id) {
    emit('reorder-column', { fromId, toId: props.column.id });
  }
};
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 w-80 h-full min-w-72 max-w-96 transition-all"
    :class="{ 'ring-2 ring-n-brand rounded-xl': isColumnDragOver }"
    @dragover="onColumnDragOver"
    @dragleave="onColumnDragLeave"
    @drop="onColumnDrop"
  >
    <div
      class="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-n-solid-1 border border-n-weak cursor-grab active:cursor-grabbing"
      draggable="true"
      @dragstart="onColumnDragStart"
    >
      <span
        class="inline-block w-3 h-3 rounded-full flex-shrink-0"
        :style="{ backgroundColor: column.color }"
      />
      <input
        v-if="isEditing"
        ref="titleInputRef"
        v-model="editValue"
        class="flex-1 min-w-0 text-sm font-semibold bg-transparent text-n-slate-12 outline-none border-b border-n-brand"
        @blur="commitEdit"
        @keydown.enter="commitEdit"
        @keydown.esc="cancelEdit"
      />
      <span
        v-else
        v-tooltip="t('KANBAN.RENAME_HINT')"
        class="flex-1 min-w-0 text-sm font-semibold text-n-slate-12 truncate select-none"
        @dblclick="startEdit"
      >
        {{ column.title }}
      </span>
      <span
        class="text-xs font-medium text-n-slate-11 bg-n-alpha-2 rounded-full px-2 py-0.5 flex-shrink-0"
      >
        {{ count }}
      </span>
      <button
        v-tooltip="t('KANBAN.ADD_CONVERSATION')"
        class="text-n-slate-10 hover:text-n-slate-12 transition-colors flex-shrink-0"
        @click="emit('add-conversation', column.id)"
      >
        <Icon icon="i-lucide-plus" class="size-4" />
      </button>
      <button
        v-tooltip="confirmingRemove ? t('KANBAN.REMOVE_COLUMN_CONFIRM') : t('KANBAN.REMOVE_COLUMN')"
        class="transition-colors flex-shrink-0"
        :class="confirmingRemove ? 'text-red-500' : 'text-n-slate-10 hover:text-n-slate-12'"
        @click="confirmRemove"
      >
        <Icon :icon="confirmingRemove ? 'i-lucide-check' : 'i-lucide-trash-2'" class="size-4" />
      </button>
    </div>

    <div
      class="flex-1 overflow-y-auto overflow-x-hidden rounded-xl transition-colors duration-150 min-h-32"
      :class="[
        isDragOver
          ? 'bg-n-alpha-blue2 border-2 border-dashed border-n-brand'
          : 'bg-n-alpha-1 border-2 border-transparent',
      ]"
      @dragover="onCardDragOver"
      @dragleave="onCardDragLeave"
      @drop="onCardDrop"
    >
      <div v-if="isLoading" class="flex flex-col gap-2 p-2">
        <div
          v-for="i in 3"
          :key="i"
          class="h-24 rounded-lg bg-n-alpha-2 animate-pulse"
        />
      </div>

      <div
        v-else-if="conversations.length === 0"
        class="flex flex-col items-center justify-center py-12 gap-3 px-4 text-center"
      >
        <div class="rounded-full bg-n-alpha-2 p-4">
          <Icon icon="i-lucide-inbox" class="text-n-slate-9 size-8" />
        </div>
        <span class="text-sm text-n-slate-11">{{ t('KANBAN.EMPTY_COLUMN') }}</span>
        <span class="text-xs text-n-slate-9">{{ t('KANBAN.EMPTY_COLUMN_HINT') }}</span>
        <button
          class="text-xs px-3 py-1.5 rounded-lg bg-n-brand text-white hover:opacity-90 transition-opacity"
          @click="emit('add-conversation', column.id)"
        >
          {{ t('KANBAN.EMPTY_COLUMN_ACTION') }}
        </button>
      </div>

      <transition-group
        v-else
        tag="div"
        enter-active-class="transition-all duration-200 ease-out"
        leave-active-class="transition-all duration-200 ease-out absolute"
        enter-from-class="opacity-0 -translate-y-2"
        leave-to-class="opacity-0 -translate-y-2"
        move-class="transition-transform duration-200 ease-out"
        class="flex flex-col gap-2 p-2"
      >
        <div
          v-for="conversation in conversations"
          :key="conversation.id"
          v-tooltip.right="buildTooltip(conversation)"
          class="group flex flex-col gap-1.5 px-3 py-3 rounded-lg cursor-pointer bg-n-solid-1 hover:bg-n-solid-2 hover:shadow-md transition-all duration-150 shadow-sm border border-n-weak hover:border-n-brand/40"
          draggable="true"
          @dragstart="e => onCardDragStart(e, conversation)"
          @click="e => openConversation(e, conversation)"
        >
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2 min-w-0">
              <Avatar
                :name="getContactName(conversation)"
                :src="getContactThumbnail(conversation)"
                :size="22"
                :status="getContactStatus(conversation)"
                rounded-full
              />
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ getContactName(conversation) }}
              </span>
            </div>
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <span
                v-if="getPriorityMeta(conversation)"
                v-tooltip="t(`KANBAN.PRIORITY.${getPriorityMeta(conversation).label}`)"
                class="inline-flex items-center justify-center size-5 rounded-full"
                :class="getPriorityMeta(conversation).color"
              >
                <Icon
                  :icon="getPriorityMeta(conversation).icon"
                  class="size-3 text-white"
                />
              </span>
              <div
                v-tooltip.left="getInboxName(conversation)"
                class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5"
              >
                <Icon
                  :icon="getInboxIcon(conversation)"
                  class="flex-shrink-0 text-n-slate-11 size-3"
                />
              </div>
            </div>
          </div>

          <p class="text-xs text-n-slate-10 line-clamp-2 mb-0 leading-relaxed">
            {{ getConversationPreview(conversation) }}
          </p>

          <div class="flex items-center justify-between gap-2 mt-0.5">
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="text-xs text-n-slate-8 flex-shrink-0">#{{ conversation.display_id }}</span>
              <span class="text-xs text-n-slate-9 flex-shrink-0">·</span>
              <span class="text-xs text-n-slate-9 truncate">{{ getLastActivity(conversation) }}</span>
            </div>
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <Avatar
                v-if="getAssignee(conversation).name"
                v-tooltip="`${t('KANBAN.ASSIGNED_TO')}: ${getAssignee(conversation).name}`"
                :name="getAssignee(conversation).name"
                :src="getAssignee(conversation).thumbnail"
                :size="18"
                :status="getAssignee(conversation).status"
                rounded-full
              />
              <div
                v-if="conversation.unread_count > 0 || conversation.unreadCount > 0"
                v-tooltip="t('KANBAN.UNREAD')"
                class="inline-flex items-center justify-center rounded-full min-w-5 h-5 px-1.5 bg-red-500"
              >
                <span class="text-xs font-semibold text-white leading-none">
                  {{ conversation.unread_count || conversation.unreadCount }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </transition-group>
    </div>
  </div>
</template>

